export type ActivityCoverCharacterEntry = {
  readonly id: string;
  readonly path: string;
  readonly categoryIds: readonly string[];
  readonly alt: string;
};

/**
 * Catálogo versionado de capas com personagens da Gamefik.
 *
 * Os arquivos vivem no próprio Manager para que cada ambiente (local, HMG e
 * produção) sirva a mesma versão da arte. Ao aplicar uma capa, o caminho é
 * convertido em URL absoluta; assim os clientes mobile não recebem uma URL
 * relativa ao Manager.
 */
export const ACTIVITY_COVER_CHARACTER_CATALOG: readonly ActivityCoverCharacterEntry[] = [
  {
    id: "english-study-group",
    path: "/activity-covers/characters/english-study-group.webp",
    categoryIds: ["ingles", "idiomas"],
    alt: "Personagens da Gamefik estudando inglês em grupo",
  },
  {
    id: "english-conversation",
    path: "/activity-covers/characters/english-conversation.webp",
    categoryIds: ["ingles", "idiomas"],
    alt: "Personagens da Gamefik praticando conversação em inglês",
  },
  {
    id: "english-library",
    path: "/activity-covers/characters/english-library.webp",
    categoryIds: ["ingles", "idiomas", "literatura"],
    alt: "Personagens da Gamefik lendo juntos na biblioteca",
  },
  {
    id: "english-vocabulary",
    path: "/activity-covers/characters/english-vocabulary.webp",
    categoryIds: ["ingles", "idiomas"],
    alt: "Personagens da Gamefik aprendendo vocabulário com cartas",
  },
  {
    id: "science-lab",
    path: "/activity-covers/characters/science-lab.webp",
    categoryIds: ["ciencias"],
    alt: "Personagens da Gamefik realizando uma experiência no laboratório",
  },
  {
    id: "math-problem-solving",
    path: "/activity-covers/characters/math-problem-solving.webp",
    categoryIds: ["matematica"],
    alt: "Personagens da Gamefik resolvendo um problema de matemática",
  },
  {
    id: "coding-club",
    path: "/activity-covers/characters/coding-club.webp",
    categoryIds: ["informatica", "programacao", "tecnologia"],
    alt: "Personagens da Gamefik aprendendo programação em grupo",
  },
  {
    id: "geography-explorers",
    path: "/activity-covers/characters/geography-explorers.webp",
    categoryIds: ["geografia"],
    alt: "Personagens da Gamefik explorando mapas e geografia",
  },
  {
    id: "history-discovery",
    path: "/activity-covers/characters/history-discovery.webp",
    categoryIds: ["historia"],
    alt: "Personagens da Gamefik investigando uma descoberta histórica",
  },
  {
    id: "art-studio",
    path: "/activity-covers/characters/art-studio.webp",
    categoryIds: ["artes"],
    alt: "Personagens da Gamefik criando juntos em um ateliê de arte",
  },
  {
    id: "music-practice",
    path: "/activity-covers/characters/music-practice.webp",
    categoryIds: ["artes", "musica"],
    alt: "Personagens da Gamefik praticando música em sala de aula",
  },
  {
    id: "writing-workshop",
    path: "/activity-covers/characters/writing-workshop.webp",
    categoryIds: ["redacao", "literatura"],
    alt: "Personagens da Gamefik participando de uma oficina de escrita",
  },
  {
    id: "class-presentation",
    path: "/activity-covers/characters/class-presentation.webp",
    categoryIds: [],
    alt: "Personagens da Gamefik apresentando um trabalho para a turma",
  },
  {
    id: "team-project",
    path: "/activity-covers/characters/team-project.webp",
    categoryIds: [],
    alt: "Personagens da Gamefik colaborando em um projeto escolar",
  },
  {
    id: "nature-study",
    path: "/activity-covers/characters/nature-study.webp",
    categoryIds: ["ciencias"],
    alt: "Personagens da Gamefik observando e estudando a natureza",
  },
  {
    id: "study-planning",
    path: "/activity-covers/characters/study-planning.webp",
    categoryIds: [],
    alt: "Personagens da Gamefik organizando um plano de estudos",
  },
];

export function resolveActivityCoverAssetUrl(path: string, origin?: string): string {
  if (/^https?:\/\//i.test(path)) return path;

  const runtimeOrigin = origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  if (!runtimeOrigin) return path;

  return new URL(path, runtimeOrigin).toString();
}

export function getCharacterActivityCoverUrls(options?: {
  readonly categoryId?: string;
  readonly origin?: string;
}): string[] {
  const categoryId = options?.categoryId?.trim();
  return ACTIVITY_COVER_CHARACTER_CATALOG
    .filter((entry) => !categoryId || entry.categoryIds.length === 0 || entry.categoryIds.includes(categoryId))
    .map((entry) => resolveActivityCoverAssetUrl(entry.path, options?.origin));
}
